import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ScrollToTop from "./ScrollToTop";
import ThemeToggleButton from "./ThemeToggleButton";

import "../styles/Editorial.css";
import "../styles/SelectionTooltip.css";

import {
  loadFromCache,
  saveToCache,
  getTimestamp,
  setTimestamp,
  isFresh,
} from "../utils/cache";
import extractOutletCode from "../utils/extractOutletCode";

const EDITORIALS_CACHE_KEY = "cachedEditorials";
const EDITORIALS_TIMESTAMP_KEY = "editorialsTimestamp";
const OUTLETS_CACHE_KEY = "cachedOutlets";
const OUTLETS_TIMESTAMP_KEY = "outletsTimestamp";
const DATE_NAV_DAYS = 7;

const shuffleArray = (list) => {
  const copy = Array.isArray(list) ? [...list] : [];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const formatKoreanDate = (raw) => {
  if (!raw) return "";
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const [, year, month, day] = match;
  const monthNum = Number(month);
  const dayNum = Number(day);
  if (Number.isNaN(monthNum) || Number.isNaN(dayNum)) {
    return "";
  }
  return `${year}년 ${monthNum}월 ${dayNum}일`;
};

const normalizeEditorial = (doc) => ({
  id:
    doc.id ??
    doc._id ??
    doc.link ??
    doc.sourceUrl ??
    doc.source_url ??
    `${doc.clusterId ?? doc.cluster_id ?? "unclustered"}-${doc.title}`,
  clusterId: doc.clusterId ?? doc.cluster_id ?? -1,
  representativeTitle:
    doc.representativeTitle ?? doc.representative_title ?? doc.title,
  title: doc.title,
  summary: doc.summary ?? null,
  sourceUrl: doc.sourceUrl ?? doc.source_url ?? doc.link ?? null,
  link: doc.link ?? doc.sourceUrl ?? doc.source_url ?? null,
  image_url: doc.image_url ?? doc.imageUrl ?? null,
  publishedAt:
    doc.publishedAt ?? doc.published_at ?? doc.date ?? null,
  createdAt: doc.date ?? doc.createdAt ?? doc.created_at ?? null,
});

const normalizeEditorialList = (list) =>
  Array.isArray(list) ? list.map((item) => normalizeEditorial(item)) : [];

const normalizeOutletMap = (raw) => {
  const map = {};
  const entries = Array.isArray(raw) ? raw : Object.values(raw || {});
  entries.forEach((item) => {
    if (!item?.id) return;
    map[item.id] = {
      ...item,
      biasCategory:
        item.biasCategory ||
        item?.bias?.category ||
        map[item.id]?.biasCategory ||
        null,
    };
  });
  return map;
};

const ISSUE_REFRESH_SCHEDULE = [
  { hour: 10, minute: 0 },
  { hour: 18, minute: 0 },
];
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const buildScheduledDate = (baseDate, { hour, minute }) =>
  new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate(),
    hour,
    minute,
    0,
    0
  );

const getExpectedEditorialCutoff = (now = new Date()) => {
  const morningCutoff = buildScheduledDate(now, ISSUE_REFRESH_SCHEDULE[0]);
  const eveningCutoff = buildScheduledDate(now, ISSUE_REFRESH_SCHEDULE[1]);

  if (now.getTime() >= eveningCutoff.getTime()) {
    return eveningCutoff;
  }
  if (now.getTime() >= morningCutoff.getTime()) {
    return morningCutoff;
  }
  const yesterday = new Date(now.getTime() - DAY_IN_MS);
  return buildScheduledDate(yesterday, ISSUE_REFRESH_SCHEDULE[1]);
};

const getLatestEditorialDate = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  let latest = 0;
  list.forEach((item) => {
    const candidate = item?.publishedAt || item?.date || item?.createdAt;
    if (!candidate) return;
    const ts = Date.parse(candidate);
    if (Number.isNaN(ts)) return;
    if (ts > latest) {
      latest = ts;
    }
  });
  return latest ? new Date(latest) : null;
};

const isEditorialDataFresh = (list) => {
  const latest = getLatestEditorialDate(list);
  if (!latest) return false;
  const cutoff = getExpectedEditorialCutoff();
  return latest.getTime() >= cutoff.getTime();
};

export default function Editorial() {
  const [editorials, setEditorials] = useState(() => {
    const cached = loadFromCache(EDITORIALS_CACHE_KEY);
    if (Array.isArray(cached) && cached.length > 0 && isEditorialDataFresh(cached)) {
      return normalizeEditorialList(cached);
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(() => editorials.length === 0);
  const [activeTab, setActiveTab] = useState("grouped");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedClusterId, setSelectedClusterId] = useState(null);
  const [shuffledOthers, setShuffledOthers] = useState([]);
  const [selectionTooltip, setSelectionTooltip] = useState(null);
  const editorialContentRef = useRef(null);
  const [outletMap, setOutletMap] = useState(() => {
    const cached = loadFromCache(OUTLETS_CACHE_KEY);
    const ts = getTimestamp(OUTLETS_TIMESTAMP_KEY);
    if (cached && isFresh(ts)) {
      return normalizeOutletMap(cached);
    }
    return {};
  });

  useEffect(() => {
    if (Object.keys(outletMap).length > 0) {
      return;
    }

    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/outlets/compact");
        if (!res.ok) {
          throw new Error("언론사 정보를 불러오지 못했습니다.");
        }
        const data = await res.json();
        const outletsMap = normalizeOutletMap(data);
        if (!alive) return;
        saveToCache(OUTLETS_CACHE_KEY, outletsMap);
        setTimestamp(OUTLETS_TIMESTAMP_KEY);
        setOutletMap(outletsMap);
      } catch (error) {
        if (alive) {
          console.error("Failed to load outlets for bias mapping:", error);
          setOutletMap({});
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [outletMap]);

  const hideSelectionTooltip = useCallback(() => {
    setSelectionTooltip(null);
  }, []);

  useEffect(() => {
    const handleMouseUp = () => {
      window.setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          hideSelectionTooltip();
          return;
        }

        const rawText = selection.toString().trim();
        if (!rawText) {
          hideSelectionTooltip();
          return;
        }

        const normalizedText = rawText.replace(/\s+/g, " ");
        if (normalizedText.length > 48) {
          hideSelectionTooltip();
          return;
        }
        const words = normalizedText.split(" ");
        if (words.length > 8) {
          hideSelectionTooltip();
          return;
        }

        if (selection.rangeCount === 0) {
          hideSelectionTooltip();
          return;
        }

        const range = selection.getRangeAt(0);
        const containerNode =
          range.commonAncestorContainer?.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentNode
            : range.commonAncestorContainer;

        if (
          !editorialContentRef.current ||
          !containerNode ||
          !editorialContentRef.current.contains(containerNode)
        ) {
          hideSelectionTooltip();
          return;
        }

        const rects = range.getClientRects();
        const rect =
          rects.length > 0
            ? rects[rects.length - 1]
            : range.getBoundingClientRect();

        if (!rect || (rect.width === 0 && rect.height === 0)) {
          hideSelectionTooltip();
          return;
        }

        const windowWidth =
          window.innerWidth || document.documentElement.clientWidth;
        const tooltipWidth = 280;
        const tooltipHeight = 44;

        const preferredTop = rect.top - tooltipHeight - 12;
        const top = preferredTop > 0 ? preferredTop : rect.bottom + 12;

        let left = rect.left + rect.width / 2;
        left = Math.max(tooltipWidth / 2 + 16, left);
        left = Math.min(windowWidth - tooltipWidth / 2 - 16, left);

        setSelectionTooltip({
          text: normalizedText,
          top,
          left,
        });
      }, 0);
    };

    const handleKeyUp = (event) => {
      if (event.key === "Escape") {
        hideSelectionTooltip();
      }
    };

    const handleScroll = () => {
      hideSelectionTooltip();
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hideSelectionTooltip]);

  useEffect(() => {
    const cached = loadFromCache(EDITORIALS_CACHE_KEY);
    const hasFreshCache =
      Array.isArray(cached) &&
      cached.length > 0 &&
      isEditorialDataFresh(cached);

    if (hasFreshCache) {
      if (editorials.length === 0) {
        setEditorials(normalizeEditorialList(cached));
      }
      setIsLoading(false);
      return;
    }

    localStorage.removeItem(EDITORIALS_CACHE_KEY);
    localStorage.removeItem(EDITORIALS_TIMESTAMP_KEY);

    let alive = true;
    setIsLoading(true);

    (async () => {
      try {
        const response = await fetch("/api/editorials/compact");
        if (!response.ok) {
          throw new Error("사설 데이터를 불러오지 못했습니다.");
        }
        const data = await response.json();
        const normalized = (Array.isArray(data) ? data : []).map(
          normalizeEditorial
        );
        if (!alive) return;
        setEditorials(normalized);
        saveToCache(EDITORIALS_CACHE_KEY, normalized);
        setTimestamp(EDITORIALS_TIMESTAMP_KEY);
      } catch (error) {
        if (alive) {
          console.error("Error fetching editorials:", error);
        }
      } finally {
        if (alive) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [editorials.length]);

  const processedEditorials = useMemo(() => {
    if (!editorials || editorials.length === 0) {
      return { grouped: [], others: [] };
    }

    const clustered = editorials.filter(
      (doc) => doc.clusterId !== undefined && doc.clusterId !== -1
    );
    const others = editorials.filter((doc) => doc.clusterId === -1);

    const groups = clustered
      .slice()
      .reduce((acc, doc) => {
        const existing = acc.find((group) => group.clusterId === doc.clusterId);
        if (existing) {
          existing.editorials.push(doc);
          if (!existing.startDate || new Date(doc.publishedAt || doc.createdAt || 0) < new Date(existing.startDate)) {
            existing.startDate = doc.publishedAt || doc.createdAt || existing.startDate;
          }
        } else {
          acc.push({
            clusterId: doc.clusterId,
            representativeTitle: doc.representativeTitle || doc.title,
            editorials: [doc],
            startDate: doc.publishedAt || doc.createdAt || null,
          });
        }
        return acc;
      }, [])
      .sort((a, b) => {
        const countDiff = b.editorials.length - a.editorials.length;
        if (countDiff !== 0) return countDiff;
        return (a.clusterId ?? 0) - (b.clusterId ?? 0);
      });

    return { grouped: groups, others };
  }, [editorials]);

  const editorialsByDate = useMemo(() => {
    if (!editorials || editorials.length === 0) {
      return [];
    }

    const groups = new Map();

    editorials.forEach((doc) => {
      const raw = doc.publishedAt;
      if (!raw) return;
      const dateObj = new Date(raw);
      if (Number.isNaN(dateObj.getTime())) return;
      const key = dateObj.toISOString().slice(0, 10);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(doc);
    });

    const sorted = Array.from(groups.entries()).sort((a, b) =>
      a[0] > b[0] ? -1 : a[0] < b[0] ? 1 : 0
    );

    return sorted.map(([date, items]) => ({
      date,
      items: items.sort((a, b) => {
        const left = new Date(b.publishedAt || 0).getTime();
        const right = new Date(a.publishedAt || 0).getTime();
        return left - right;
      }),
    }));
  }, [editorials]);

  const formatDateLabel = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "short",
      });
    } catch (e) {
      return dateString;
    }
  };

  useEffect(() => {
    if (editorialsByDate.length === 0) {
      setSelectedDate(null);
      return;
    }

    setSelectedDate((prev) => {
      if (prev && editorialsByDate.some((group) => group.date === prev)) {
        return prev;
      }
      return editorialsByDate[0].date;
    });
  }, [editorialsByDate]);

  useEffect(() => {
    if (activeTab === "daily" && editorialsByDate.length > 0 && !selectedDate) {
      setSelectedDate(editorialsByDate[0].date);
    }
  }, [activeTab, editorialsByDate, selectedDate]);

  useEffect(() => {
    const topics = processedEditorials.grouped;
    if (!topics || topics.length === 0) {
      setSelectedClusterId(null);
      return;
    }

    setSelectedClusterId((prev) => {
      if (prev && topics.some((topic) => topic.clusterId === prev)) {
        return prev;
      }
      return topics[0].clusterId;
    });
  }, [processedEditorials.grouped]);

  useEffect(() => {
    if (!processedEditorials.others || processedEditorials.others.length === 0) {
      setShuffledOthers([]);
      return;
    }
    setShuffledOthers(processedEditorials.others);
  }, [processedEditorials.others]);

  const totalCount = editorials.length;

  const latestEditorialDate = useMemo(
    () => getLatestEditorialDate(editorials),
    [editorials]
  );

  const latestEditorialLabel = useMemo(() => {
    if (!latestEditorialDate) return null;
    const pad = (value) => String(value).padStart(2, "0");
    const year = latestEditorialDate.getUTCFullYear();
    const month = pad(latestEditorialDate.getUTCMonth() + 1);
    const day = pad(latestEditorialDate.getUTCDate());
    const hour = pad(latestEditorialDate.getUTCHours());
    const minute = pad(latestEditorialDate.getUTCMinutes());
    return `${year}.${month}.${day} ${hour}:${minute} KST`;
  }, [latestEditorialDate]);

  const handleShuffleOthers = () => {
    const source =
      shuffledOthers.length > 0
        ? shuffledOthers
        : processedEditorials.others;
    if (!source || source.length < 2) {
      return;
    }
    setShuffledOthers(shuffleArray(source));
  };

  const getEditorialsByBias = (editorialsInGroup) => {
    const byBias = {
      progressive: [],
      centrist: [],
      conservative: [],
      unknown: [],
    };

    editorialsInGroup.forEach((item) => {
      const outletCode = extractOutletCode(
        item.sourceUrl || item.link,
        outletMap
      );
      const bias =
        outletMap[outletCode]?.biasCategory ||
        outletMap[outletCode]?.bias?.category ||
        "모름";

      switch (bias) {
        case "진보":
          byBias.progressive.push(item);
          break;
        case "보수":
          byBias.conservative.push(item);
          break;
        case "중도":
          byBias.centrist.push(item);
          break;
        default:
          byBias.unknown.push(item);
          break;
      }
    });

    return byBias;
  };

  const renderGroupedView = () => {
    if (
      processedEditorials.grouped.length === 0 &&
      processedEditorials.others.length === 0
    ) {
      return <div className="ed-empty">표시할 사설이 없습니다.</div>;
    }

    const topics = processedEditorials.grouped;
    const activeTopic =
      topics.find((topic) => topic.clusterId === selectedClusterId) || topics[0] || null;
    const activeByBias = activeTopic
      ? getEditorialsByBias(activeTopic.editorials)
      : { progressive: [], centrist: [], conservative: [], unknown: [] };

    return (
      <>
        <div className="ed-topic-view">
          <div className="ed-topic-list" role="list">
            {topics.map((topic) => {
              const counts = getEditorialsByBias(topic.editorials);
              const total = topic.editorials.length;
              const isActive = activeTopic && topic.clusterId === activeTopic.clusterId;

              return (
                <button
                  key={topic.clusterId}
                  type="button"
                  className={`ed-topic-card ${isActive ? "active" : ""}`}
                  onClick={() => setSelectedClusterId(topic.clusterId)}
                  role="listitem"
                  aria-pressed={isActive}
                >
                  <span className="ed-topic-card__title">
                    {topic.representativeTitle}
                  </span>
                  <span className="ed-topic-card__date">
                    {topic.startDate 
                      ? formatDateLabel(topic.startDate)
                      : "시작일 정보 없음"} 
                  </span>
                  <span className="ed-topic-card__total">{total.toLocaleString()}건</span>
                  <div className="ed-topic-card__counts">
                    <span className="ed-topic-card__count progressive">
                      진보 {counts.progressive.length}
                    </span>
                    <span className="ed-topic-card__count centrist">
                      중도 {counts.centrist.length}
                    </span>
                    <span className="ed-topic-card__count conservative">
                      보수 {counts.conservative.length}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="ed-topic-detail">
            {activeTopic ? (
              <>
                <h3 className="ed-topic-detail__title">{activeTopic.representativeTitle}</h3>
                <div className="ed-bias-grid">
                  <div className="bias-column">
                    <h4 className="bias-column-title progressive">진보</h4>
                    <div className="bias-card-list">
                      {activeByBias.progressive.map((item, idx) => (
                        <EditorialCard
                          key={`${item.id}-p-${idx}`}
                          item={item}
                          outletMap={outletMap}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="bias-column">
                    <h4 className="bias-column-title centrist">중도</h4>
                    <div className="bias-card-list">
                      {activeByBias.centrist.map((item, idx) => (
                        <EditorialCard
                          key={`${item.id}-c-${idx}`}
                          item={item}
                          outletMap={outletMap}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="bias-column">
                    <h4 className="bias-column-title conservative">보수</h4>
                    <div className="bias-card-list">
                      {activeByBias.conservative.map((item, idx) => (
                        <EditorialCard
                          key={`${item.id}-s-${idx}`}
                          item={item}
                          outletMap={outletMap}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="ed-topic-detail__placeholder">
                관심 있는 주제를 선택하면 세부 사설을 확인할 수 있습니다.
              </div>
            )}
          </div>
        </div>

        {processedEditorials.others.length > 0 && (
          <div className="ed-others-section">
            <div className="ed-others-header">
              <h3 className="ed-others-title">그 외 주제 사설</h3>
              <div className="ed-others-meta">
                <span className="ed-others-count">
                  {processedEditorials.others.length.toLocaleString()}건
                </span>
                <button
                  type="button"
                  className="ed-others-shuffle"
                  onClick={handleShuffleOthers}
                  disabled={processedEditorials.others.length < 2}
                  aria-label="그 외 사설 순서를 랜덤으로 재정렬"
                >
                  순서 셔플
                </button>
              </div>
            </div>
            <div className="ed-mini-grid">
              {(shuffledOthers.length > 0
                ? shuffledOthers
                : processedEditorials.others
              ).map((item, idx) => (
                <EditorialMiniCard
                  key={`${item.id}-o-${idx}`}
                  item={item}
                  outletMap={outletMap}
                />
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderDateView = () => {
    if (editorialsByDate.length === 0) {
      return <div className="ed-empty">표시할 사설이 없습니다.</div>;
    }

    const navDates = editorialsByDate.slice(0, DATE_NAV_DAYS);
    const activeGroup =
      editorialsByDate.find((group) => group.date === selectedDate) ||
      navDates[0] ||
      editorialsByDate[0];

    if (!activeGroup) {
      return <div className="ed-empty">표시할 사설이 없습니다.</div>;
    }

    return (
      <div className="ed-date-view">
        <div className="ed-date-nav" role="tablist" aria-label="날짜 선택">
          {navDates.map(({ date }) => (
            <button
              key={date}
              type="button"
              className={`ed-date-nav__button ${
                date === (activeGroup?.date || selectedDate) ? "active" : ""
              }`}
              onClick={() => setSelectedDate(date)}
              role="tab"
              aria-selected={date === (activeGroup?.date || selectedDate)}
            >
              {formatDateLabel(date)}
            </button>
          ))}
        </div>

        <div className="ed-date-panel">
          <div className="ed-date-panel__header">
            <h3 className="ed-date-panel__title">
              {formatDateLabel(activeGroup.date)}
            </h3>
            <span className="ed-date-panel__count">
              {activeGroup.items.length.toLocaleString()}건
            </span>
          </div>
          <div className="ed-mini-grid">
            {activeGroup.items.map((item, idx) => (
              <EditorialMiniCard
                key={`${item.id}-d-${idx}`}
                item={item}
                outletMap={outletMap}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="ed-wrap" ref={editorialContentRef}>
        <div className="ed-header">
          <div className="ed-header-main">
            <h2>{activeTab === "grouped" ? "주제별 사설" : "날짜별 사설"}</h2>
            <div className="ed-header-meta">
              {/* {latestEditorialLabel && (
                <span className="ed-updated-at">
                  최신 업데이트 {latestEditorialLabel}
                </span>
              )} */}
              <span className="ed-count">
                총 {totalCount.toLocaleString()}건
              </span>
            </div>
          </div>
          <div className="ed-tabs" role="tablist" aria-label="사설 보기 방식">
            <button
              type="button"
              className={`ed-tab ${activeTab === "grouped" ? "active" : ""}`}
              onClick={() => setActiveTab("grouped")}
              role="tab"
              aria-selected={activeTab === "grouped"}
            >
              주제별 사설
            </button>
            <button
              type="button"
              className={`ed-tab ${activeTab === "daily" ? "active" : ""}`}
              onClick={() => setActiveTab("daily")}
              role="tab"
              aria-selected={activeTab === "daily"}
            >
              날짜별 사설
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="ed-loading">데이터를 불러오는 중...</div>
        ) : activeTab === "grouped" ? (
          renderGroupedView()
        ) : (
          renderDateView()
        )}
        {selectionTooltip && (
          <div
            className="selection-tooltip"
            style={{
              top: `${selectionTooltip.top}px`,
              left: `${selectionTooltip.left}px`,
            }}
          >
            <span className="selection-tooltip__label">
              {selectionTooltip.text.length > 18
                ? `${selectionTooltip.text.slice(0, 18)}…`
                : selectionTooltip.text}
            </span>
            <a
              href={`https://namu.wiki/w/${encodeURIComponent(
                selectionTooltip.text
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="selection-tooltip__link"
              onClick={() => {
                window.setTimeout(() => {
                  hideSelectionTooltip();
                }, 0);
              }}
            >
              나무위키에서 보기
            </a>
          </div>
        )}
      </div>
      <ThemeToggleButton />
      <ScrollToTop />
    </div>
  );
}

function EditorialCard({ item, outletMap }) {
  const fallback = "/favicon.ico";
  const [imgSrc, setImgSrc] = useState(item.image_url || fallback);

  const outletCode = useMemo(
    () => extractOutletCode(item.source_url || item.link, outletMap),
    [item.sourceUrl, item.link, outletMap]
  );
  const bias =
    outletMap[outletCode]?.biasCategory ||
    outletMap[outletCode]?.bias?.category ||
    "모름";

  const getBiasClass = (label) => {
    switch (label) {
      case "진보":
        return "bias-progressive";
      case "보수":
        return "bias-conservative";
      default:
        return "bias-centrist";
    }
  };

  const formattedDate = useMemo(
    () => formatKoreanDate(item.publishedAt),
    [item.publishedAt]
  );

  return (
    <a
      className="ed-card"
      href={item.link || item.sourceUrl || "#"}
      target="_blank"
      rel="noreferrer noopener"
    >
      {/* <div className="ed-thumb">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={item.title}
            onError={() => setImgSrc(fallback)}
          />
        ) : (
          <div className="ed-thumb-ph" />
        )}
      </div> */}

      <div className="ed-content">
        <div className="ed-meta">
          <span className={`ed-outlet ${getBiasClass(bias)}`}>
            {outletMap[outletCode]?.name || outletCode || "알 수 없음"}
          </span>
        </div>

        <h3 className="ed-title">{item.title}</h3>
        {formattedDate && <p className="ed-date">{formattedDate}</p>}
      </div>
    </a>
  );
}

function EditorialMiniCard({ item, outletMap }) {
  const outletCode = useMemo(
    () => extractOutletCode(item.sourceUrl || item.link, outletMap),
    [item.sourceUrl, item.link, outletMap]
  );

  const bias =
    outletMap[outletCode]?.biasCategory ||
    outletMap[outletCode]?.bias?.category ||
    "모름";

  const badgeClass =
    bias === "진보"
      ? "ed-mini-card__badge--progressive"
      : bias === "보수"
      ? "ed-mini-card__badge--conservative"
      : bias === "중도"
      ? "ed-mini-card__badge--centrist"
      : "ed-mini-card__badge--unknown";

  const formattedDate = useMemo(
    () => formatKoreanDate(item.publishedAt),
    [item.publishedAt]
  );

  const outletName =
    outletMap[outletCode]?.name || outletCode || item.sourceUrl || "알 수 없음";

  return (
    <a
      className="ed-mini-card"
      href={item.link || item.sourceUrl || "#"}
      target="_blank"
      rel="noreferrer noopener"
    >
      <div className="ed-mini-card__header">
        <span className={`ed-mini-card__badge ${badgeClass}`}>{bias}</span>
        {formattedDate && (
          <time className="ed-mini-card__time" dateTime={item.publishedAt}>
            {formattedDate}
          </time>
        )}
      </div>
      <h4 className="ed-mini-card__title">{item.title}</h4>
      <span className="ed-mini-card__outlet">{outletName}</span>
    </a>
  );
}
